import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'
import ProductForm from './ProductForm'


export default function ProductsTable() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)

  const fetchProducts = async () => {
    const { data } = await supabase
      .from('products')
      .select('*, product_categories(name), product_types(name)')
      .order('created_at', { ascending: false })
    if (data) setProducts(data)
    setLoading(false)
  }

  useEffect(() => { fetchProducts() }, [])

  const handleToggle = async (product) => {
    await supabase
      .from('products')
      .update({ available: !product.available })
      .eq('id', product.id)
    fetchProducts()
  }

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return
    await supabase.from('products').delete().eq('id', product.id)
    fetchProducts()
  }

  const handleEdit = (product) => {
    setEditing(product)
    setShowForm(true)
  }

  const handleAdd = () => {
    setEditing(null)
    setShowForm(true)
  }

  const handleClose = () => {
    setShowForm(false)
    setEditing(null)
    fetchProducts()
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-gray-500 text-sm">{products.length} product{products.length !== 1 ? 's' : ''}</p>
        <button
          onClick={handleAdd}
          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
        >
          Add Product
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm py-10 text-center">Loading...</div>
      ) : products.length === 0 ? (
        <div className="text-gray-400 text-sm py-10 text-center border border-dashed border-gray-200 dark:border-white/10">
          No products yet. Click Add Product to get started.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {products.map(product => (
            <div
              key={product.id}
              className={`flex items-center gap-4 px-4 py-3 border transition-colors ${
                product.available
                  ? 'bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10'
                  : 'bg-gray-50 dark:bg-white/2 border-gray-100 dark:border-white/5 opacity-60'
              }`}
            >
              {/* Image */}
              <div className="w-12 h-12 bg-gray-200 dark:bg-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                {product.image_url
                  ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                  : <span className="text-gray-400 text-xs">No img</span>
                }
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm text-gray-900 dark:text-white uppercase tracking-tight truncate">
                  {product.name}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                  <span>ZMW {Number(product.price).toFixed(2)}</span>
                  {product.product_categories?.name && (
                    <span className="bg-gray-200 dark:bg-white/10 px-1.5 py-0.5">{product.product_categories.name}</span>
                  )}
                  {product.product_types?.name && (
                    <span className="bg-gray-200 dark:bg-white/10 px-1.5 py-0.5">{product.product_types.name}</span>
                  )}
                  <span className={product.stock <= 5 ? 'text-red-500 font-bold' : ''}>
                    Stock: {product.stock}
                  </span>
                </div>
              </div>

              {/* Status badge */}
              <div className={`text-xs font-bold uppercase tracking-widest px-2 py-1 shrink-0 hidden sm:block ${
                product.available
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400'
              }`}>
                {product.available ? 'Live' : 'Hidden'}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => handleToggle(product)}
                  className="text-xs text-blue-500 hover:text-blue-400 font-bold uppercase tracking-widest transition-colors"
                >
                  {product.available ? 'Hide' : 'Show'}
                </button>
                <span className="text-gray-300 dark:text-white/20">|</span>
                <button
                  onClick={() => handleEdit(product)}
                  className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 font-bold uppercase tracking-widest transition-colors"
                >
                  Edit
                </button>
                <span className="text-gray-300 dark:text-white/20">|</span>
                <button
                  onClick={() => handleDelete(product)}
                  className="text-xs text-red-500 hover:text-red-400 font-bold uppercase tracking-widest transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ProductForm
          product={editing}
          onClose={handleClose}
        />
      )}
    </div>
  )
}